import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { UIStateProps, uiState } from '../../../atoms/uiAtom';
import { useNavigate, useParams } from 'react-router-dom';

import Container from '../Container';
import DateNavigator from '../DateNavigator';
import { EanURL } from '../../../common/eanUtils';
import { ReactComponent as InformationIcon } from '../../../assets/information.svg';
import MeterIndexDetailValueItem from './MeterIndexDetailValueltem';
import ModalInformation from '../../modals/ModalInformation';
import { OVERVIEW_TABS } from '../../../enum/OverviewTabsEnums';
import SdpHeader from '../SdpHeader';
import Skeleton from 'react-loading-skeleton';
import { VolumeTypeEnum } from '../../../enum/VolumeTypeEnum';
import { routes } from '../../../routes';
import { useGetIndex } from '../../../hooks/meters';
import { useGetSdp } from '../../../hooks/sdps';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

export default function MeterIndexDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const [isOpenModalInformation, setIsOpenModalInformation] = useState(false);

  const { t } = useTranslation();
  const {
    isFetching: isFetchingSdp,
    isError: isErrorGetSdp,
    data: sdp,
  } = useGetSdp(params.id ? (EanURL.decrypt(params.id) as string) : params.id);
  const {
    isFetching: isFetchingIndex,
    isError: isErrorGetIndex,
    data: indexes,
  } = useGetIndex({
    sdpId: sdp?.data?.id,
    startDate: sdp?.data.initialLoadDate,
    endDate: new Date().toISOString(),
  });
  const setUiState = useSetRecoilState<UIStateProps>(uiState);

  useEffect(() => {
    setUiState((prev) => ({
      ...prev,
      hasError: isErrorGetSdp || isErrorGetIndex,
    }));
  }, [isErrorGetSdp, isErrorGetIndex, setUiState]);

  const handleClickBack = () => {
    navigate(
      `${routes.withArgs(
        `${routes.OVERVIEW.path}${routes.OVERVIEW.children.OVERVIEW_DETAIL.path}`,
        {
          ':id': EanURL.encrypt(sdp?.data.id!) as string,
          ':tabId': OVERVIEW_TABS.INDEX,
        }
      )}${window.location.search}`
    );
  };

  const hasInjection = () => indexes?.data[params.indexId as any]?.injection;

  const navigateIndex = (id: number) => {
    if (sdp?.data!.id && params.indexId) {
      navigate(
        `${routes.withArgs(
          `${routes.METERS.path}${routes.METERS.children.METERS_DETAIL.path}${routes.METERS.children.METERS_INDEX_DETAIL.path}`,
          {
            ':id': EanURL.encrypt(sdp?.data.id!) as string,
            ':indexId': id.toString(),
          }
        )}${window.location.search}`
      );
    }
  };
  const handleClickNextIndex = () => {
    navigateIndex(+params.indexId! + 1);
  };

  const handleClickPrevIndex = () => {
    navigateIndex(+params.indexId! - 1);
  };

  const handleClickActionInfo = () => {
    setIsOpenModalInformation((prev) => !prev);
  };

  const hasTHIndex = useMemo(() => {
    return (
      params.indexId &&
      indexes?.data[params.indexId as any]?.indexes.some(
        (index) =>
          index.type.includes(VolumeTypeEnum.CONSUMPTION_TH) ||
          index.type.includes(VolumeTypeEnum.INJECTION_TH)
      )
    );
  }, [indexes, params.indexId]);

  const getInfoContent = () => (
    <Fragment>
      <p className="mb-2 text-start">
        <strong>{t('modal.meter.index.gaz.one')}</strong>
        {t('modal.meter.index.gaz.two')}
      </p>
      <p className="mb-2 text-start">
        <strong>{t('modal.meter.index.elec.one')}</strong>
        {t('modal.meter.index.elec.two')}
      </p>
      <p className="mb-2 text-start">
        <strong>{t('modal.meter.index.energy.sharing.one')}</strong>{' '}
      </p>
      <p className="mb-2 text-start">
        <strong>{t('modal.meter.index.energy.sharing.two')}</strong>
        {t('modal.meter.index.energy.sharing.three')}
      </p>
      <p className="text-start">
        <strong>{t('modal.meter.index.energy.sharing.four')}</strong>
        {t('modal.meter.index.energy.sharing.five')}
      </p>
    </Fragment>
  );

  return (
    <section className="container-fluid  meter-index-detail h-100">
      <div className="row">
        <div className="col-12">
          <Container className="meter-index-detail__content">
            <SdpHeader sdp={sdp?.data} onClickBack={handleClickBack} />
            <DateNavigator
              onClickNext={handleClickNextIndex}
              onClickPrev={handleClickPrevIndex}
              hasNextDisabled={
                params.indexId && indexes?.data!
                  ? +params.indexId === indexes?.data.length - 1
                  : true
              }
              hasPrevDisabled={params.indexId ? +params.indexId === 0 : true}
              startDate={indexes?.data[params.indexId as any]?.startDate}
              endDate={indexes?.data[params.indexId as any]?.endDate}
            />
            {!isFetchingIndex && !isFetchingSdp && !isErrorGetIndex && !isErrorGetSdp && (
              <Fragment>
                <div className="widget meter-index-detail__item col-12 mb-4">
                  <h2 className="meter-index-detail__item__title">
                    {t('overview.meter.detail.index.detail.total.off.take')}

                    <InformationIcon
                      role="button"
                      className="icon icon--action"
                      onClick={handleClickActionInfo}
                    />
                  </h2>
                  <div className="row">
                    {params.indexId &&
                      indexes?.data[params.indexId as any]?.indexes
                        .filter((index) => index.type.includes(VolumeTypeEnum.CONSUMPTION))
                        .filter((index) => {
                          if (hasTHIndex) {
                            return index.type.includes(VolumeTypeEnum.CONSUMPTION_TH);
                          }
                          return index;
                        })
                        .sort((a, b) => {
                          if (a.type === VolumeTypeEnum.CONSUMPTION_DAY) {
                            return -1;
                          } else if (b.type === VolumeTypeEnum.CONSUMPTION_DAY) {
                            return 1;
                          } else if (a.type === VolumeTypeEnum.CONSUMPTION_NIGHT) {
                            return -1;
                          } else if (b.type === VolumeTypeEnum.CONSUMPTION_NIGHT) {
                            return 1;
                          } else if (a.type === VolumeTypeEnum.CONSUMPTION_EXCLUSIVE_NIGHT) {
                            return -1;
                          } else if (b.type === VolumeTypeEnum.CONSUMPTION_EXCLUSIVE_NIGHT) {
                            return 1;
                          }
                          return 0;
                        })
                        .map((index, key) => (
                          <MeterIndexDetailValueItem
                            key={key}
                            division={sdp?.data.division}
                            index={index}
                            value={indexes?.data[params.indexId as any].consumption}
                          />
                        ))}
                  </div>
                </div>
                {hasInjection() && (
                  <div className="widget meter-index-detail__item  col-12">
                    <h2 className="meter-index-detail__item__title">
                      {t('overview.meter.detail.index.detail.total.injection')}
                      <InformationIcon
                        role="button"
                        className="icon icon--action"
                        onClick={handleClickActionInfo}
                      />
                    </h2>
                    <div className="row">
                      {params.indexId &&
                        indexes?.data[params.indexId as any]?.indexes
                          .filter((index) => index.type.includes(VolumeTypeEnum.INJECTION))
                          .filter((index) => {
                            if (hasTHIndex) {
                              return index.type.includes(VolumeTypeEnum.INJECTION_TH);
                            }
                            return index;
                          })
                          .sort((a, b) => {
                            if (a.type === VolumeTypeEnum.INJECTION_DAY) {
                              return -1;
                            } else if (b.type === VolumeTypeEnum.INJECTION_DAY) {
                              return 1;
                            } else if (a.type === VolumeTypeEnum.INJECTION_NIGHT) {
                              return -1;
                            } else if (b.type === VolumeTypeEnum.INJECTION_NIGHT) {
                              return 1;
                            } else if (a.type === VolumeTypeEnum.INJECTION_EXCLUSIVE_NIGHT) {
                              return -1;
                            } else if (b.type === VolumeTypeEnum.INJECTION_EXCLUSIVE_NIGHT) {
                              return 1;
                            }
                            return 0;
                          })
                          .map((index, key) => (
                            <MeterIndexDetailValueItem
                              key={key}
                              division={sdp?.data.division}
                              index={index}
                              value={indexes?.data[params.indexId as any].injection}
                            />
                          ))}
                    </div>
                  </div>
                )}
              </Fragment>
            )}
            {(isFetchingIndex || isFetchingSdp || isErrorGetIndex || isErrorGetSdp) && (
              <Fragment>
                <div className="row meter-index-detail__loader mb-5">
                  {Array.from(Array(3).keys()).map((_, index) => (
                    <div key={index} className="meter-index-detail__loader__item col-12 col-md-4">
                      <Skeleton count={1} width="50%" className="mb-4" height={16} />
                      <Skeleton count={1} width="25%" className="mb-2" height={20} />
                      <div className="d-flex flex-row">
                        <div className="w-100">
                          <Skeleton count={1} width="30%" height={10} />
                          <Skeleton count={1} width="25%" height={10} />
                        </div>
                        <div className="w-100">
                          <Skeleton count={1} width="30%" height={10} />
                          <Skeleton count={1} width="25%" height={10} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="row meter-index-detail__loader">
                  {Array.from(Array(3).keys()).map((_, index) => (
                    <div key={index} className="meter-index-detail__loader__item col-12 col-md-4">
                      <Skeleton count={1} width="50%" className="mb-4" height={16} />
                      <Skeleton count={1} width="25%" className="mb-2" height={20} />
                      <div className="d-flex flex-row">
                        <div className="w-100">
                          <Skeleton count={1} width="30%" height={10} />
                          <Skeleton count={1} width="25%" height={10} />
                        </div>
                        <div className="w-100">
                          <Skeleton count={1} width="30%" height={10} />
                          <Skeleton count={1} width="25%" height={10} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Fragment>
            )}
          </Container>
        </div>
      </div>
      <ModalInformation isOpen={isOpenModalInformation} onClickAction={handleClickActionInfo}>
        {getInfoContent()}
      </ModalInformation>
    </section>
  );
}
